import React, { useContext, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { MapDrawFuncParams, CFCProps, AddressType } from 'types';
import { addAlert } from 'redux/session/actions';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import { v4 as UUIDV4 } from 'uuid';
import { addGraphic, getLiveArea, deleteGraphics } from './helper';
import { MapGraphicError } from './error';
import {
  setShape,
  setMultipleShapes,
  removeShape,
  queryRecentLayerExtentStart,
  setLiveArea
} from '../../localStore/actions';
import {
  LocalDispatchContext,
  LocalStoreContext
} from '../../localStore/context';
import { useEmptyCallback } from 'utilities/hooks.util';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import DrawingTooltip from '../drawingTooltip';

const { modules } = MapSettings;
const extraMapModules = [
  modules.featureLayer,
  modules.polygon,
  modules.baseLayerView2D,
  modules.handles,
  modules.projection,
  modules.watchUtils,
  modules.layer,
  modules.tileInfo,
  modules.webMercatorUtils
];

export const MapView: React.FC<Props> = ({
  onClickTools,
  setMapReady,
  onClickMap,
  onEdit,
  onPointerOverUserRef,
  activeTool,
  showDrawingTooltip,
  setDuplicatedShapeMapId,
  setShowDrawingTooltip
}) => {
  const dispatch = useDispatch();
  const localDispatch = useContext(LocalDispatchContext);
  const localStore = useContext(LocalStoreContext);
  const { duplicateEnquiry, draft } = localStore;

  const onDrawing = (params: MapDrawFuncParams) => {
    setShowDrawingTooltip(true);
    const geometryArea = getLiveArea(params);
    geometryArea > 0 && localDispatch(setLiveArea(geometryArea));
  };

  const onDeleteDrawing = (params: MapDrawFuncParams) => {
    deleteGraphics(params);
    if (Array.isArray(params.graphics)) {
      params.graphics.forEach((g) => {
        localDispatch(removeShape(g.uid));
      });
    }
  };

  const onCancelDrawing = () => {
    onClickTools('selector');
    localDispatch(setLiveArea(undefined));
    setShowDrawingTooltip(false);
  };

  const onCompleteDrawing = (params: MapDrawFuncParams) => {
    onEdit(false);
    onClickTools('selector');
    localDispatch(setLiveArea(undefined));
    setShowDrawingTooltip(false);
    try {
      const { polygonInGeoJson, centroid, geometryArea, uid } =
        addGraphic(params);
      localDispatch(
        setShape({
          geoJson: polygonInGeoJson,
          area: geometryArea,
          centroid,
          shapeId: UUIDV4(),
          uid
        })
      );
    } catch (e) {
      if (e instanceof MapGraphicError) {
        e.uid && localDispatch(removeShape(e.uid));
        dispatch(addAlert('error', e.name, e.message));
      }
    }
  };

  const dispatchDuplicatedGraphic = useEmptyCallback(
    ({ polygonInGeoJson, centroid, geometryArea, uid }: any) => {
      localDispatch(
        setShape({
          geoJson: polygonInGeoJson,
          area: geometryArea,
          centroid,
          shapeId: UUIDV4(),
          uid
        })
      );
    }
  );

  const dispatchSetMultipleShapes = useEmptyCallback((shapes: any[]) => {
    localDispatch(setMultipleShapes(shapes));
  });

  const onMapReady = () => {
    const { loadedMap, loadedMapModules, loadedView } = window;
    if (loadedMap) {
      const { Config, FeatureLayer, GraphicsLayer } = loadedMapModules;
      Config.request.interceptors.push({
        urls: getFeatureServerUrl('Enquiries'),
        before: featureRequestInterceptors
      });

      const featureLayer = new FeatureLayer({
        id: 'recent-enquiries-layer',
        url: getFeatureServerUrl('Enquiries'),
        outFields: ['objectid'],
        visible: false
      });

      const errorShapeLayer = new GraphicsLayer({ id: 'error-shape-layer' });
      const errorMarkerLayer = new GraphicsLayer({ id: 'error-marker-layer' });
      const shapeReferLayer = new FeatureLayer({
        id: 'shape-refer-layer',
        minScale: 250000,
        geometryType: 'polygon',
        labelsVisible: true,
        labelingInfo: [
          {
            labelExpressionInfo: { expression: '$feature.NAME' },
            symbol: {
              type: 'text',
              color: '#161616',
              font: {
                family: 'IBMPlexMono-Bold',
                size: 10
              },
              haloColor: 'white',
              haloSize: 1,
              yoffset: -5,
              verticalAlignment: 'middle'
            }
          }
        ],
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            color: [0, 0, 0, 0],
            style: 'solid',
            outline: {
              style: 'none'
            }
          }
        },
        fields: [
          {
            name: 'ObjectID',
            alias: 'ObjectID',
            type: 'oid'
          },
          {
            name: 'Name',
            alias: 'Name',
            type: 'string'
          }
        ],
        objectIdField: 'ObjectID',
        spatialReference: { wkid: 4326 },
        source: []
      });

      loadedMap.addMany([
        featureLayer,
        errorShapeLayer,
        shapeReferLayer,
        errorMarkerLayer
      ]);

      featureLayer.when(() => {
        localDispatch(queryRecentLayerExtentStart(featureLayer));
      });
      loadedView.ui.move('zoom', 'bottom-left');
    }
    setMapReady(true);
  };

  useEffect(() => {
    if (duplicateEnquiry?.shape) {
      const { loadedMapModules, loadedMap } = window;
      const { Graphic, WebMercatorUtils } = loadedMapModules;
      const drawingLayer = loadedMap.findLayerById('drawing-layer');

      const geometry = geojsonToArcGIS(duplicateEnquiry.shape);
      const webMercatorGeometry =
        WebMercatorUtils.geographicToWebMercator(geometry);

      const graphic = new Graphic({
        geometry: {
          ...webMercatorGeometry,
          type: 'polygon'
        },
        symbol: MapSettings.polygonSymbol
      });
      zoomToFeature(graphic.geometry.extent, 0, true);
      const result = addGraphic({
        graphic,
        drawingLayer,
        graphics: undefined,
        tool: 'polygon'
      });
      dispatchDuplicatedGraphic(result);
      setDuplicatedShapeMapId(result.uid);
    }
  }, [duplicateEnquiry, dispatchDuplicatedGraphic, setDuplicatedShapeMapId]);

  useEffect(() => {
    if (draft?.shapes) {
      const { loadedMapModules, loadedMap } = window;
      const { Graphic, WebMercatorUtils } = loadedMapModules;
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      let extent: any = undefined;
      const singleAddress: AddressType = {
        line1: '',
        line2: '',
        locality: '',
        postcode: '',
        state: '',
        country: ''
      };

      if (draft?.details?.data?.addressLine1) {
        singleAddress.line1 = draft.details.data.addressLine1;
        singleAddress.locality = draft.details.data.addressLocality || '';
        singleAddress.postcode = draft.details.data.addressPostcode || '';
        singleAddress.state = draft.details.data.addressState || '';
      }
      const shapes = draft.shapes.map((s) => {
        const geometry = geojsonToArcGIS(s.shape);
        const webMercatorGeometry =
          WebMercatorUtils.geographicToWebMercator(geometry);
        const graphic = new Graphic({
          geometry: {
            ...webMercatorGeometry,
            type: 'polygon'
          },
          symbol: MapSettings.polygonSymbol
        });

        extent = extent
          ? extent.union(graphic.geometry.extent)
          : graphic.geometry.extent;

        const { centroid, geometryArea, uid } = addGraphic({
          graphic,
          drawingLayer,
          tool: 'polygon',
          graphics: undefined
        });

        return {
          shapeId: s.id,
          geoJson: s.shape,
          address:
            draft.shapes.length === 1 && draft?.details?.data?.addressLine1
              ? singleAddress
              : s.address,
          area: geometryArea,
          userReference: s.user_reference,
          centroid,
          lastSeqNo: s.seq_no,
          uid
        };
      });
      zoomToFeature(extent, 0, true);
      window.lastVisitedExtent = extent;
      window.lastVisitedZoom = undefined;
      dispatchSetMultipleShapes(shapes);
    }
  }, [draft, dispatchSetMultipleShapes]);

  return (
    <div className="map--view">
      <Map
        onDrawing={onDrawing}
        onCompleteDrawing={onCompleteDrawing}
        onEditDrawingStart={() => onEdit(true)}
        onDeleteDrawing={onDeleteDrawing}
        onCancelDrawing={onCancelDrawing}
        onReady={onMapReady}
        onSelect={onClickMap}
        onPointerOverUserRef={onPointerOverUserRef}
        extraModules={extraMapModules}
        showHomeButton={true}
        showSearchButton={true}
        showMapStyleButton={true}
        maxZoomLevel={20}
      />
      {showDrawingTooltip && (
        <DrawingTooltip
          view={window.loadedView}
          sketchView={window.loadedSketchViewModel}
          sketchToolType={activeTool}
          setShowDrawingTooltip={setShowDrawingTooltip}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ session }: RootState) => ({
  screenSize: session.screenSize
});

const connector = connect(mapStateToProps);

type ownProps = {
  onClickTools: Function;
  setMapReady: Function;
  onEdit: Function;
  onClickMap?: (...args: any[]) => any;
  onPointerOverUserRef?: (...args: any[]) => any;
  activeTool: string;
  showDrawingTooltip: boolean;
  setShowDrawingTooltip: Function;
  setDuplicatedShapeMapId: Function;
};

type Props = CFCProps<typeof connector> & ownProps;

MapView.displayName = 'LodgeEnquiryMapViewModule';

export default React.memo(connector(MapView));
